import { Alert, Input, Switch, Text, TextInput, Textarea } from "@mantine/core";
import { useState, type FormEvent } from "react";

import { isApiError } from "../../api/errors";
import { useGetBrand } from "../../api/generated/endpoints/teams/teams";
import type {
  ColorsExtract,
  ProductExtract,
  ProductIn as ProductBody,
} from "../../api/generated/model";
import {
  ColorsExtract as ColorsExtractAnswer,
  ProductExtract as ProductExtractAnswer,
  productInColorsMax,
  productInDescriptionOneMax,
  productInKeyBenefitsItemMax,
  productInKeyBenefitsMax,
  productInNameMax,
  productInOfferOneMax,
  productInPriceOneMax,
  productInTaglineOneMax,
  productInUseCasesItemMax,
  productInUseCasesMax,
} from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { LeaveGuard } from "../../platform/LeaveGuard";
import { pictureOf, type PickerLibrary } from "../../platform/media/ImagePicker";
import { importPicture } from "../../platform/media/url-import";
import { toUserMessage } from "../../platform/messages";
import { FieldLabel, infoLabel } from "../../ui/FieldLabel";
import { FormDivider } from "../../ui/FormDivider";
import { ColorsInput } from "./ColorsInput";
import { colorDrafts, fieldIssues } from "./drafts";
import classes from "./forms.module.css";
import { ImageField } from "./ImageField";
import { ImportRow } from "./ImportRow";
import { ListRows } from "./ListRows";
import { ReextractColors } from "./ReextractColors";
import { SaveButton } from "./SaveButton";
import { useExtract } from "./use-extract";
import {
  emptySlot,
  filledSlots,
  productDraft,
  productIn,
  repeatedSlot,
  sameDraft,
  type ImageSlot,
  type ProductDraft,
} from "./product-draft";

/** Live's one sentence for any failed extraction (its toast; ours a line, web/AGENTS.md). */
export const PRODUCT_EXTRACT_FAILED = "Failed to extract product";
/** Live's sentence when a picture's colors could not be read (its toast; ours a line). */
export const COLORS_EXTRACT_FAILED = "Failed to extract colors from image";
/**
 * Said when pictures the page names did not import (#1469), so a form left without its product
 * shot says why: none of the `found`, or `left` of them.
 */
export function picturesLeftOut(left: number, found: number): string {
  return left === found
    ? "The page's pictures could not be imported. Upload them by hand."
    : `${left} of the page's ${found} pictures could not be imported. Upload them by hand.`;
}
/** The longest store address the row takes, as the request's `url`. */
const URL_MAX = 2048;
import { TAG_LABEL, TagSelect } from "./TagSelect";

const MESSAGES: Record<string, string> = {
  name: `Give the product a name of up to ${productInNameMax} characters.`,
  colors: "Each color needs a hex code such as #1A2B3C.", // raw-color: an example in a hint
  key_benefits: `List up to ${productInKeyBenefitsMax} benefits of up to ${productInKeyBenefitsItemMax} characters.`,
  use_cases: `List up to ${productInUseCasesMax} use cases of up to ${productInUseCasesItemMax} characters.`,
};

export interface ProductFormProps {
  /** Saved onboarding link; extraction still waits for an explicit click. */
  initialStoreUrl?: string;
  initial: ProductDraft;
  /** A saved product: the divider says "saved product data" and the button "Save". */
  editing: boolean;
  pending: boolean;
  error: unknown;
  onSubmit: (body: ProductBody) => void;
  /** The brand's library, which the image slots upload into and choose from. */
  library: PickerLibrary;
}

/** The whole product with its images, as `POST` and `PUT` take it (A3), in the live layout. */
export function ProductForm(props: ProductFormProps) {
  const { initial, editing, pending, error, onSubmit, library } = props;
  const [draft, setDraft] = useState(initial);
  const [problems, setProblems] = useState<Record<string, string>>({});
  // Slots with an upload in flight: the form saves once they have their images.
  const [uploading, setUploading] = useState<ReadonlySet<number>>(new Set());
  // The brand's colors, which the product follows unless it has its own.
  const brandColors = useGetBrand(library.teamId, library.brandId, {
    query: { select: (brand) => colorDrafts(brand.colors) },
  }).data;
  const set = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const text = (key: keyof ProductDraft) => (event: { currentTarget: { value: string } }) =>
    set(key, event.currentTarget.value);
  const setSlot = (index: number, patch: Partial<ImageSlot>) =>
    setDraft((current) => ({
      ...current,
      images: current.images.map((slot, at) => (at === index ? { ...slot, ...patch } : slot)),
    }));
  const busy = (index: number) => (on: boolean) =>
    setUploading((current) => {
      const next = new Set(current);
      if (on) next.add(index);
      else next.delete(index);
      return next;
    });

  // Live's schema names the tag when images are there but none is the product shot (#252 item 2):
  // shown under the images, so a Save held for it says why, while editing too.
  const filled = filledSlots(draft);
  const missingShot = filled.length > 0 && !filled.some((slot) => slot.tag === "product_shot");
  // Live's "Re-extract from Image" (spec G15c): a picture's colors read in a job of the brand,
  // then taken as the product's colors, labelled as live labels them.
  const reextract = useExtract({
    teamId: library.teamId,
    brandId: library.brandId,
    answer: ColorsExtractAnswer,
    onAnswer: (found: ColorsExtract) =>
      set(
        "colors",
        found.colors
          .slice(0, productInColorsMax)
          .map((color) => ({ hex: color.hex, label: color.label })),
      ),
  });

  // Live's required fields: the name, the description and a product shot (live's schema).
  const ready =
    draft.name.trim() !== "" &&
    draft.description.trim() !== "" &&
    filledSlots(draft).some((slot) => slot.tag === "product_shot") &&
    uploading.size === 0 &&
    // A save while a picture's colors are read would lose them (AO's review of #1671).
    !reextract.running;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready) return;
    const found: Record<string, string> = {};
    const repeated = repeatedSlot(draft);
    if (repeated !== undefined) found[`image${repeated}`] = "Another image uses this asset.";
    const parsed = productIn(draft);
    if (!parsed.success) {
      const filled = filledSlots(draft);
      for (const [field, issue] of fieldIssues(parsed.error)) {
        const slot = field === "images" ? filled[Number(issue.path[1])]?.index : undefined;
        if (slot !== undefined) found[`image${slot}`] = "This image cannot be used; pick another.";
        else found[field] = MESSAGES[field] ?? "This value is not valid.";
      }
    }
    setProblems(found);
    if (parsed.success && Object.keys(found).length === 0) onSubmit(parsed.data);
  };
  const nameTaken = isApiError(error, "PRODUCT_NAME_TAKEN");

  // Live's import (#249): the store's page read in a job of the brand, then the whole form reset
  // to its fields, uploads in flight dropped, and up to five of its pictures imported into the
  // brand's library, the first the product shot, as live's slots take them; the library shows
  // each only once the save names it (G15, 9).
  const [storeUrl, setStoreUrl] = useState(props.initialStoreUrl ?? "");
  const [leftOut, setLeftOut] = useState<string | null>(null);
  const extract = useExtract({
    teamId: library.teamId,
    brandId: library.brandId,
    answer: ProductExtractAnswer,
    onAnswer: async (found: ProductExtract) => {
      setUploading(new Set());
      const images: ImageSlot[] = [];
      const wanted = found.images.slice(0, 5);
      for (const image of wanted) {
        try {
          const asset = await importPicture(library.teamId, library.brandId, image);
          const slot = emptySlot(images.length);
          images.push({ ...slot, assetId: asset.id, url: pictureOf(asset) });
        } catch {
          // A picture the store no longer serves as read is left out, and the line says so.
        }
      }
      const left = wanted.length - images.length;
      setLeftOut(left > 0 ? picturesLeftOut(left, wanted.length) : null);
      const empty = productDraft();
      setDraft({
        ...empty,
        name: found.name.slice(0, productInNameMax),
        description: found.description.slice(0, productInDescriptionOneMax),
        tagline: found.tagline.slice(0, productInTaglineOneMax),
        price: (found.price ?? "").slice(0, productInPriceOneMax),
        keyBenefits: found.key_benefits.slice(0, productInKeyBenefitsMax),
        useCases: found.use_cases.slice(0, productInUseCasesMax),
        images: empty.images.map((slot, at) => images[at] ?? slot),
      });
    },
  });

  return (
    <form className={classes.form} onSubmit={submit} noValidate>
      {/* Live's Stay or Leave while a change is not saved, an upload runs, or an import has yet
          to fill the form (spec A3d). */}
      <LeaveGuard
        when={
          !sameDraft(draft, initial) || uploading.size > 0 || extract.running || reextract.running
        }
      />
      <ImportRow
        label="Import your product using Shopify or Amazon listings"
        description="Auto-extract product info & generate description"
        field={{ value: storeUrl, onChange: setStoreUrl, maxLength: URL_MAX, error: undefined }}
        extract={{
          running: extract.running,
          onExtract: () => {
            setLeftOut(null);
            extract.start({ type: "extract.product", url: storeUrl.trim() });
          },
        }}
      />
      {extract.failed ? (
        <Alert color="red" variant="light" className={classes.alert}>
          {PRODUCT_EXTRACT_FAILED}
        </Alert>
      ) : null}
      {leftOut !== null && !extract.running ? (
        <Alert color="yellow" variant="light" className={classes.alert} data-pictures-left-out>
          {leftOut}
        </Alert>
      ) : null}
      <FormDivider
        label={
          extract.running
            ? "extracting product info"
            : editing
              ? "saved product data"
              : "or enter manually"
        }
      />
      <fieldset
        className={classes.extractable}
        disabled={extract.running}
        data-extracting={extract.running || undefined}
      >
        <div className={classes.fields}>
          <div className={classes.row}>
            <TextInput
              {...infoLabel({
                label: "Product name",
                required: true,
                info: "The primary identifier used across all creatives — appears in ad copy, video scripts, and generated visuals.",
              })}
              required
              withAsterisk={false}
              placeholder="e.g., OMG Sour Peach Gummies"
              maxLength={productInNameMax}
              value={draft.name}
              onChange={text("name")}
              error={nameTaken ? toUserMessage(error) : problems["name"]}
            />
            <TextInput
              {...infoLabel({
                label: "Tagline",
                info: "A short punchy phrase used in ad headlines and overlays to communicate your product's core value at a glance.",
              })}
              placeholder="e.g., Bold Peach Flavor, Zero Guilt."
              maxLength={productInTaglineOneMax}
              value={draft.tagline}
              onChange={text("tagline")}
            />
          </div>
          <Input.Wrapper
            label={
              <FieldLabel
                label="Product Images"
                required
                info="Upload up to 5 of your own product images & tag them. At the creative step, you choose which ones to use — select one, several, or all."
                spoken
              />
            }
            description="Required: Product shot"
            labelElement="div"
            error={missingShot ? 'At least one image must have the "Product Shot" tag' : undefined}
          >
            <div className={classes.slots}>
              {draft.images.map((slot, index) => (
                // The five slots are fixed positions, so the position is the key.
                <ImageField
                  key={index}
                  name={`image ${index + 1}`}
                  label={`Image ${index + 1}`}
                  shape="slot"
                  value={slot.assetId === "" ? null : { assetId: slot.assetId, url: slot.url }}
                  library={library}
                  onChange={(picked) =>
                    picked === null
                      ? setSlot(index, emptySlot(index))
                      : setSlot(index, { assetId: picked.assetId, url: picked.url })
                  }
                  onBusy={busy(index)}
                  error={problems[`image${index}`]}
                  caption={
                    <Text className={classes.slotCaption}>{TAG_LABEL[emptySlot(index).tag]}</Text>
                  }
                  extra={
                    <TagSelect
                      name={`Image ${index + 1} tag`}
                      value={slot.tag}
                      onChange={(tag) => setSlot(index, { tag })}
                    />
                  }
                />
              ))}
            </div>
          </Input.Wrapper>
          <div className={classes.palette}>
            <ColorsInput
              label="Color Palette"
              description={draft.useProductColors ? "Product Colors" : "Brand Colors"}
              info="Saved to your product profile and applied to creatives only when selected during the creative step."
              value={draft.useProductColors ? draft.colors : (brandColors ?? [])}
              {...(draft.useProductColors ? { onChange: (colors) => set("colors", colors) } : {})}
              max={productInColorsMax}
              error={problems["colors"]}
              trailing={
                draft.useProductColors ? (
                  <ReextractColors
                    pictures={filled.map((slot) => ({
                      assetId: slot.assetId,
                      slot: slot.index,
                      url: slot.url,
                    }))}
                    running={reextract.running}
                    disabled={uploading.size > 0 || extract.running}
                    onPick={(picture) =>
                      reextract.start({
                        type: "extract.colors",
                        source: { kind: "asset", asset_id: picture.assetId },
                      })
                    }
                  />
                ) : undefined
              }
            />
            <Switch
              className={classes.paletteSwitch}
              label="Use product colors"
              checked={draft.useProductColors}
              onChange={(event) => {
                const on = event.currentTarget.checked;
                setDraft((current) => ({
                  ...current,
                  useProductColors: on,
                  // A first switch-on starts from the brand's colors, as live does.
                  colors: on && current.colors.length === 0 ? (brandColors ?? []) : current.colors,
                }));
              }}
            />
          </div>
          {reextract.failed && draft.useProductColors ? (
            <Alert color="red" variant="light" data-colors-extract-failed>
              {COLORS_EXTRACT_FAILED}
            </Alert>
          ) : null}
          <div className={classes.row}>
            <ListRows
              label="Key Benefits"
              description={`Up to ${productInKeyBenefitsMax} benefits`}
              placeholder="Add benefit here"
              info="Used in ad copy and video scripts to clearly communicate why customers should choose your product."
              value={draft.keyBenefits}
              onChange={(rows) => set("keyBenefits", rows)}
              max={productInKeyBenefitsMax}
              itemMax={productInKeyBenefitsItemMax}
              error={problems["key_benefits"]}
            />
            <ListRows
              label="Use Cases"
              description={`Up to ${productInUseCasesMax} scenarios`}
              placeholder="Add use case here"
              info="Helps the AI generate scenario-based creatives that show your product being used in real-life contexts."
              value={draft.useCases}
              onChange={(rows) => set("useCases", rows)}
              max={productInUseCasesMax}
              itemMax={productInUseCasesItemMax}
              error={problems["use_cases"]}
            />
          </div>
          <div className={classes.row}>
            <TextInput
              {...infoLabel({
                label: "Price",
                info: "Displayed in promotional creatives and ad copy to communicate value and drive purchase decisions.",
              })}
              placeholder="e.g., $20.95"
              maxLength={productInPriceOneMax}
              value={draft.price}
              onChange={text("price")}
            />
            <TextInput
              {...infoLabel({
                label: "Current Offer",
                info: "Highlighted in ad creatives to promote active deals and increase conversion rates.",
              })}
              placeholder="e.g., Buy 2 save $4"
              maxLength={productInOfferOneMax}
              value={draft.offer}
              onChange={text("offer")}
            />
          </div>
          <Textarea
            {...infoLabel({
              label: "Description",
              required: true,
              info: "Provides the AI with full product context to generate accurate, compelling, and on-brand content across all formats.",
            })}
            placeholder="Enter detailed product description"
            required
            withAsterisk={false}
            rows={5}
            maxLength={productInDescriptionOneMax}
            value={draft.description}
            onChange={text("description")}
          />
        </div>
      </fieldset>
      {error !== null && error !== undefined && !nameTaken ? (
        <div className={classes.alert}>
          <ErrorAlert error={error} />
        </div>
      ) : null}
      <div className={classes.footer}>
        <SaveButton
          addLabel="Add Product"
          editing={editing}
          ready={ready}
          required="Complete all required fields to create your product. Required: Product Name, a Product Shot image, and Description"
          pending={pending}
          size="md"
        />
      </div>
    </form>
  );
}
